import React, { useEffect, useRef, useState } from 'react';
import Toastr from 'toastr';
import Loading from '../components/Loading';
import SessionManager from '../routes/SessionManager';
import CommonUtils from '../util/CommonUtils';
import API from '../service/SiteKickAPIService';

function AITextNotification() {

    const url_string = String(window.location);
    const url_parts = url_string.split('event/');
    let event_id = url_parts.length > 1 ? url_parts[1] : 0;
    let foundEvent = false;
    const [user, setUser] = useState({});
    const aiEventFetched = useRef(false);

    useEffect(() => {
        var sessionMgr = new SessionManager();
        let user = sessionMgr.readCookie('user', true, '/?redirectUrl=' + window.location.pathname);
        if (user !== null) {
            setUser(user);
        }
    }, []);

    useEffect(() => {
        if (!user.user_login) return; // skip empty initial state
        if (aiEventFetched.current) return; // already triggered once

        if (!user.site_locations) {
            fetchSiteLocations();
        } else {
            aiEventFetched.current = true;
            fetchAIEventDetails();
        }
    }, [user]);

    const fetchSiteLocations = () => {
        var data = {
            customer_id: user.customer_id,
            site_id: user.user_sites,
            user_login: user.user_login,
            user_token: user.user_token
        }

        CommonUtils.ajax(
            {
                url: API.SITE.BASE,
                type: 'POST',
                contentType: "application/json; charset=utf-8",
                dataType: "json",
                data: JSON.stringify(data)
            }
        ).then(
            (res) => {
                if (res.status && res.status.code === 200) {
                    let site_locations = res.data ? res.data.filter(x => x.site_state.trim().toUpperCase() !== 'INACTIVE') : [];
                    let loc = 'site_name';
                    site_locations = site_locations.sort((a, b) => a[loc] > b[loc] ? 1 : a[loc] < b[loc] ? -1 : 0);

                    let userCopy = JSON.parse(JSON.stringify(user));
                    userCopy.site_locations = site_locations;

                    if (site_locations && site_locations.length > 0) {
                        let siteDefault = getDefaultSite(site_locations);
                        if (siteDefault) {
                            userCopy.site_selected = siteDefault;
                            var sessionMgr = new SessionManager();
                            sessionMgr.storeCookie('user', userCopy);
                        }

                        setUser(userCopy);
                    } else {
                        sessionMgr.storeCookie('user', user);
                        Toastr.error('No Sites Found ...');
                        window.location = './message';
                    }
                }
            },
        );
    }

    const getDefaultSite = (site_locations) => {
        var siteDefault = null;
        let defaultSiteId = user.site_id; //Default Site;

        if (user.site_selected)
            defaultSiteId = user.site_selected.site_id;

        if (defaultSiteId) {
            for (let s of site_locations)
                if (String(s.site_id) === String(defaultSiteId)) {
                    siteDefault = s;
                    break;
                }
        }

        if (!siteDefault)
            siteDefault = site_locations[0];

        return siteDefault;
    }

    const fetchAIEventDetails = () => {
        var data = {
            user_login: user.user_login,
            user_token: user.user_token,
            source: "D",
            criteria: {
                from: 0,
                size: 1,
                event_id: event_id
            }
        };

        CommonUtils.ajax(
            {
                url: API.COMMON_TABLE_OPERATIONS.EVENT_TMP.FETCH,
                type: 'POST',
                contentType: "application/json; charset=utf-8",
                dataType: "json",
                data: JSON.stringify(data)
            }
        ).then(
            async (res) => {
                if (res && res.status && res.status.code === 200) {
                    var data = res.data.records;
                    let event_record = data.length > 0 ? data[0] : null;
                    if (event_record === null) {
                        Toastr.error('Notification link expired or invalid ...');
                        setTimeout(() => { window.location = '../dashboard'; }, 4000);
                        return;
                    }

                    let _user = JSON.parse(JSON.stringify(user));
                    _user.ai_event = event_record;

                    // Check user is authorized for this site
                    const userSites = Array.isArray(user.user_sites) ? user.user_sites : (user.user_sites ? [user.user_sites] : []);
                    const isAuthorized = userSites.some(id => String(id) === String(event_record.site_id));
                    if (!isAuthorized) {
                        Toastr.error('Not authorized to view notification details ...');
                        setTimeout(() => { window.location = '../dashboard'; }, 4000);
                        return;
                    }

                    // Try to find site from already-loaded session first, else fetch directly by site_id
                    let site = user.site_locations && user.site_locations.find(s => String(s.site_id) === String(event_record.site_id));
                    if (!site) {
                        site = await CommonUtils.getSite(event_record.site_id, user);
                    }

                    if (!site) {
                        Toastr.error('Not authorized to view notification details ...');
                        setTimeout(() => { window.location = '../dashboard'; }, 4000);
                        return;
                    }

                    _user.site_selected = site;

                    // Fetch zones for the event's site
                    let zones = await fetchSiteLocationZones(site.site_id);
                    _user.zones = zones;

                    // Find zone by location_id from event
                    let _zone_selected = zones.find(z => z.location_id === event_record.location_id);

                    // If zone still not found, fetch location directly
                    if (!_zone_selected && event_record.location_id) {
                        _zone_selected = await CommonUtils.getLocation(event_record.location_id, { ...user, site_selected: site });
                    }

                    if (_zone_selected) {
                        _user.zone_selected = _zone_selected;
                    }

                    var sessionMgr = new SessionManager();
                    sessionMgr.storeCookie('user', _user);

                    if (event_record.link_level === "sensor") {
                        window.location = '../sensors-dashboard';
                    } else if (event_record.link_level === "site") {
                        window.location = '../dashboard';
                    } else {
                        if (!_zone_selected) {
                            Toastr.error('Not authorized to view notification details ...');
                            setTimeout(() => { window.location = '../dashboard'; }, 4000);
                            return;
                        }
                        window.location = '../dashboard/zone-history';
                    }

                } else {
                    Toastr.error('Notification link expired or invalid ...');
                    setTimeout(() => { window.location = '../dashboard'; }, 4000);
                }
            }
        );
    }

    const fetchSiteLocationZones = async (site_id = user.site_selected.site_id) => {
        return new Promise((resolve, reject) => {
            var data = {
                customer_id: user.customer_id,
                site_id: site_id,
                user_login: user.user_login,
                user_token: user.user_token
            }
            CommonUtils.ajax(
                {
                    url: API.LOCATION.BASE,
                    type: 'POST',
                    contentType: "application/json; charset=utf-8",
                    dataType: "json",
                    data: JSON.stringify(data)
                }
            ).then(
                (res) => {
                    if (res && res.status && res.status.code === 200) {
                        if (res.data && res.data.length > 0) {
                            let zone_prefs = user.zone_prefs;
                            if (zone_prefs && zone_prefs.length > 0) {
                                res.data.sort((a, b) => {
                                    return zone_prefs.indexOf(a.location_id) - zone_prefs.indexOf(b.location_id);
                                });
                            }
                            resolve(res.data)
                        } else {
                            resolve([]);
                        }
                    } else {
                        resolve([]);
                    }
                },
                (res) => {
                    resolve([]);
                });
        });
    }

    return (
        <React.Fragment>
            <div id="loginContainer" className="container-fluid" style={{ marginTop: '80px' }}>
                {!foundEvent && <Loading>Fetching Notification Event Information ...</Loading>}
            </div>
        </React.Fragment>
    );
}

export default AITextNotification;